import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import React from 'react';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { components } from 'react-select';

const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_PUBLIC_API_KEY;

export const isGoogleMapsConfigured = (): boolean => Boolean(googleMapsApiKey);

let hasWarnedMissingGoogleMapsApiKey = false;

interface GooglePlacesAutocompleteProps {
  value: any;
  onChange: (place: any) => void;
  onInputChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  className?: string;
}

const CustomDropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <MagnifyingGlassIcon className="size-5 text-secondary-text" />
    </components.DropdownIndicator>
  );
};

export const GooglePlacesAutocompleteInput: React.FC<
  GooglePlacesAutocompleteProps
> = ({
  value,
  onChange,
  onInputChange,
  placeholder = 'Search for an address...',
  label,
  className
}) => {
  if (!isGoogleMapsConfigured()) {
    if (!hasWarnedMissingGoogleMapsApiKey) {
      hasWarnedMissingGoogleMapsApiKey = true;
      console.warn(
        'GooglePlacesAutocompleteInput: VITE_GOOGLE_MAPS_PUBLIC_API_KEY is not set. Address autocomplete is disabled.'
      );
    }

    return null;
  }

  return (
    <div className={className}>
      {label && (
        <label className="block font-medium mb-2 text-secondary-text">
          {label}
        </label>
      )}
      <GooglePlacesAutocomplete
        apiKey={googleMapsApiKey}
        selectProps={{
          value,
          onChange,
          onInputChange: (newValue) => {
            onInputChange(newValue);
          },
          placeholder,
          noOptionsMessage: () => null,
          components: {
            DropdownIndicator: CustomDropdownIndicator,
            IndicatorSeparator: null
          },
          styles: {
            control: (provided: any, state: any) => ({
              ...provided,
              position: 'relative',
              zIndex: 10,
              backgroundColor: 'var(--color-white)',
              border: '1px solid var(--color-gray-4)',
              borderRadius: '0.5rem',
              boxShadow: 'none',
              minHeight: '46px',
              height: '46px',
              '&:hover': {
                backgroundColor: 'var(--color-white)',
                border: '1px solid #000'
              },
              ...(state.isFocused && {
                backgroundColor: 'var(--color-white)',
                border: '1px solid #000',
                boxShadow: 'none'
              })
            }),
            menu: (provided: any) => ({
              ...provided,
              zIndex: 9999
            }),
            input: (provided: any) => ({
              ...provided,
              color: 'var(--color-primary-text)',
              fontSize: '0.875rem',
              fontWeight: 500,
              fontFamily: 'Figtree, sans-serif',
              margin: 0,
              padding: 0
            }),
            placeholder: (provided: any) => ({
              ...provided,
              color: 'var(--color-gray-4)',
              fontSize: '0.875rem',
              fontWeight: 500
            }),
            option: (provided: any, state: any) => ({
              ...provided,
              color: 'var(--color-primary-text)',
              backgroundColor: state.isSelected
                ? 'var(--color-gray-2)'
                : state.isFocused
                  ? 'var(--color-gray-2)'
                  : 'var(--color-white)',
              fontWeight: 500,
              fontSize: '0.875rem',
              cursor: 'pointer'
            }),
            singleValue: (provided: any) => ({
              ...provided,
              color: 'var(--color-primary-text)',
              fontSize: '0.875rem',
              fontWeight: 500
            })
          }
        }}
      />
    </div>
  );
};

export default GooglePlacesAutocompleteInput;
